import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import TeamLionImg from "../../assets/carousel/image2.jpg";
import BannerImg from "../../assets/ClubTeamsBanner.jpeg";
import { MdAdd, MdRemove, MdChevronRight, MdSchool, MdPalette, MdPublic } from 'react-icons/md';
import { BsWhatsapp } from "react-icons/bs";
import { Link } from 'react-router-dom';

import { HouseSystemGallery, AcademicClubsGallery, CreativeClubsGallery } from '../../utils/galleryAssets';



const CollageGallery = ({ images }) => {
    if (!images || images?.length === 0) {
        return (
            <div className="h-[300px] lg:h-[350px] bg-gray-50 rounded border-2 border-dashed border-gray-200 flex items-center justify-center">
                <span className="text-gray-400 font-medium italic">Clubs in action... Coming Soon</span>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {images.map((img, index) => (
                <div 
                    key={index} 
                    className="h-48 md:h-64 rounded-xl overflow-hidden shadow-md transform hover:scale-[1.02] transition-transform duration-500"
                >
                    <img src={img} className="w-full h-full object-cover" alt={`Gallery ${index + 1}`} />
                </div>
            ))}
        </div>
    );
};

const AccordionItem = ({ title, content, isOpen, onClick, teams, images, points, icon: Icon }) => {
    return (
        <div className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30 transition-colors">
            <button
                className={`w-full flex items-center justify-between py-4 px-3 lg:px-5 transition-all duration-500 ${isOpen ? 'bg-[#021d53] text-white shadow-xl' : 'bg-white text-[#021d53]'}`}
                onClick={onClick}
            >
                <div className="flex items-center gap-2">
                    {Icon && <Icon className={`text-xl ${isOpen ? 'text-white' : 'text-[#ed3137]'}`} />}
                    <span className="text-xl font-black uppercase tracking-tight text-left">{title}</span>
                </div>
                {isOpen ? <MdRemove className="text-4xl" /> : <MdAdd className="text-4xl" />}
            </button>
            <div 
                className={`overflow-hidden transition-all duration-700 ease-in-out ${isOpen ? 'max-h-[3500px] opacity-100' : 'max-h-0 opacity-0'}`}
            >
                <div className="p-8 lg:p-16 bg-white">
                    <div className="flex flex-col gap-12 items-start">
                        <div className=" space-y-8">
                            <p className="text-gray-600 leading-relaxed">
                                {content}
                            </p>
                            {teams && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {teams.map((team, idx) => (
                                        <div key={idx} className="flex items-center gap-3 bg-gray-50 p-4 rounded border-l-4 border-[#ed3137]">
                                            <MdChevronRight className="text-[#ed3137]" />
                                            <span className="font-bold text-[#021d53]">{team}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {points && (
                                <ul className="space-y-4">
                                    {points.map((point, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <div className="mt-1.5 w-2 h-2 rounded-full bg-[#ed3137] shrink-0" />
                                            <span className="text-gray-700 font-semibold">{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Collage Gallery */}
                        {images && images.length > 0 && (
                            <div className="w-full">
                                <CollageGallery images={images} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ClubsAndTeams = () => {
    const location = useLocation();
    const [openIndex, setOpenIndex] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);

    const sections = [
        {
            title: "The Team System",
            icon: MdPublic,
            content: "The school community is strengthened through four vibrant Teams. The team system fosters belonging, teamwork, and healthy competition. Through inter-team sports, quizzes, debates, and cultural activities, learners build school spirit while developing resilience and collaboration skills.",
            teams: ["Team Eagle", "Team Lion", "Team Sharks", "Team Raven"],
            images: HouseSystemGallery
        },
        {
            title: "Academic Clubs",
            icon: MdSchool,
            content: "Academic clubs provide opportunities for intellectual exploration beyond the classroom. These clubs sharpen critical thinking and build confidence by allowing students to dive deeper into specialized fields of knowledge.",
            points: ["Debate and Public Speaking", "Science and Innovation Projects", "ICT and Coding Activities", "Literary Engagement"],
            images: AcademicClubsGallery
        },
        {
            title: "Creative & Interest-Based Clubs",
            icon: MdPalette,
            content: "Creative expression is an essential part of the Anell 360. Through artistic discovery, students discover and refine their talents in a supportive environment that encourages out-of-the-box thinking.",
            points: ["Music & Instrumentation", "Contemporary & Traditional Dance", "Drama and Theater Arts", "Visual Arts & Design"],
            images: CreativeClubsGallery
        },
        {
            title: "Service & Values-Based Clubs",
            icon: MdPublic,
            content: "Character formation is central to the school’s philosophy. Service-oriented clubs promote leadership, responsibility, and global citizenship. Learners are encouraged to think beyond themselves and contribute positively to society.",
            points: ["Leadership & Prefectship", "Environmental Awareness", "Community Outreach", "Responsibility & Character Building"],
            images: []
        }
    ];

    return (
        <div className="font-sans text-gray-900 bg-white min-h-screen">
            

            {/* Hero Section */}
            <div className="relative h-[60vh] lg:h-[75vh] w-full flex items-center justify-center overflow-hidden bg-[#221b2d]">
                <img 
                    src={BannerImg} 
                    alt="Clubs & Teams Banner" 
                    className="absolute inset-0 h-full w-full object-cover opacity-50 scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#221b2d]/20 to-[#221b2d]/90"></div>
                <div className="relative z-10 text-center px-5 max-w-5xl">
                    <h1 className="text-5xl md:text-8xl font-black text-white mb-6 uppercase tracking-tighter drop-shadow-2xl">
                        Clubs & Teams
                    </h1>
                    <div className="w-32 h-2 bg-[#ed3137] mx-auto mb-8"></div>
                    <p className="text-xl md:text-2xl text-blue-50 font-medium max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
                        Fostering school spirit, teamwork, and healthy competition through a vibrant house system and diverse interest clubs.
                    </p>
                </div>
            </div>

            {/* Content Section */}
            <section className="py-24 lg:py-32 max-w-[1440px] mx-auto px-5 md:px-10 lg:px-20">
                <div className="mb-20">
                    <h2 className="text-4xl md:text-6xl font-black text-[#021d53] mb-8 leading-tight">Clubs & Teams System</h2>
                    <div className="w-16 h-1 bg-[#ed3137] mb-10"></div>
                    <p className="max-w-3xl text-xl text-gray-600">The Anell 360 experience extends through our rich diversity of clubs and the heartbeat of our house system.</p>
                </div>

                <div className="bg-white rounded shadow-[0_20px_100px_rgba(0,0,0,0.06)] overflow-hidden border border-gray-100">
                    {sections.map((section, index) => (
                        <AccordionItem 
                            key={index}
                            title={section.title}
                            icon={section.icon}
                            content={section.content}
                            teams={section.teams}
                            points={section.points}
                            images={section.images}
                            isOpen={openIndex === index}
                            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                        />
                    ))}
                </div>
            </section>

            {/* Spirit Section */}
            <section className="py-32 bg-[#021d53] text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none">
                    <img src={TeamLionImg} className="w-full h-full object-cover" alt="Spirit Background" />
                </div>
                <div className="max-w-[1440px] mx-auto px-5 md:px-20 relative z-10">
                    <div className="max-w-3xl">
                        <h2 className="text-4xl md:text-6xl font-black mb-10 text-[#ed3137]">Unity In Diversity</h2>
                        <p className="text-xl md:text-2xl leading-relaxed text-blue-50 font-light italic border-l-8 border-[#ed3137] pl-8">
                            "Beyond the competition, our team system is the heartbeat of our community. It's where friendships are forged, leadership is tested, and every student finds a family within our school family."
                        </p>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="py-24 bg-white text-center px-5">
                <div className="max-w-4xl mx-auto py-20 bg-gray-50 rounded-[4rem] border-2 border-dashed border-gray-200">
                    <h3 className="text-3xl font-bold text-[#021d53] mb-8 uppercase tracking-widest">Experience Our Spirit</h3>
                    <div className="flex flex-wrap justify-center gap-6">
                        <Link to="/contact-us" className="bg-[#021d53] text-white px-12 py-5 rounded-full font-bold text-lg hover:bg-[#ed3137] transition-all transform hover:scale-105">
                            Join Anell
                        </Link>
                        <Link to="/news-events/calendar" className="border-2 border-[#021d53] text-[#021d53] px-12 py-5 rounded-full font-bold text-lg hover:bg-[#021d53] hover:text-white transition-all transform hover:scale-105">
                            View Events
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
            
        </div>
    );
};

export default ClubsAndTeams;
